import React, { useEffect, useMemo, useState } from "react";
import { useAxiosInstance } from "../../../services/Interceptor";
import { useDispatch, useSelector } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faTrash } from "@fortawesome/free-solid-svg-icons";
import { fetchOffers, deleteOfferAction } from "../../../store/AthleteActions";
import { getOfferAcceptanceStatus } from "../../../services/AthleteService";
import AddOfferModal from "./AddOfferModal";
import DeleteOfferModal from "./DeleteOfferModal";
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from "@headlessui/react";
import { useForm } from "react-hook-form";

function OfferAcceptanceModal({ open, onClose, onSubmit, initialValues }) {
  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: { offerAcceptanceStatus: "no", offerAcceptanceSchool: "" },
  });

  useEffect(() => {
    if (open) {
      reset({
        offerAcceptanceStatus: initialValues?.offerAcceptanceStatus || "no",
        offerAcceptanceSchool: initialValues?.offerAcceptanceSchool || "",
      });
    }
  }, [open, initialValues, reset]);

  const offerAcceptanceStatus = watch("offerAcceptanceStatus");

  return (
    <Dialog open={open} onClose={onClose} className="relative z-[350]">
      <DialogBackdrop className="fixed inset-0 bg-black/40" />
      <div className="fixed inset-0 flex w-screen items-center justify-center p-4">
        <DialogPanel className="w-full max-w-2xl bg-white rounded-lg shadow-xl">
          <div className="p-6">
            <DialogTitle className="text-lg font-bold mb-4">
              Offer Acceptance Details
            </DialogTitle>
            <form
              onSubmit={handleSubmit((data) =>
                onSubmit(
                  data.offerAcceptanceStatus,
                  data.offerAcceptanceSchool,
                ),
              )}
            >
              <div className="mt-6 flex gap-4 items-end">
                <div className="form-field flex-1">
                  <select
                    id="offerAcceptanceStatus"
                    {...register("offerAcceptanceStatus")}
                    className="bg-gray-50 border border-gray-300 text-md rounded-md form-select appearance-none bg-no-repeat text-gray-900 w-full p-2"
                  >
                    <option value="no">No</option>
                    <option value="Committed">
                      I have made a verbal commitment
                    </option>
                    <option value="Signed">
                      I have signed a letter of intent
                    </option>
                  </select>
                </div>
                <div className="form-field flex-1">
                  <input
                    type="text"
                    id="offerAcceptanceSchool"
                    placeholder="School name"
                    disabled={offerAcceptanceStatus === "no"}
                    {...register("offerAcceptanceSchool", {
                      validate: (v) =>
                        offerAcceptanceStatus === "no" ||
                        !!v?.trim() ||
                        "School is required",
                    })}
                    className={`border rounded p-2 w-full ${
                      offerAcceptanceStatus === "no"
                        ? "opacity-50 cursor-not-allowed"
                        : ""
                    }`}
                  />
                  {errors.offerAcceptanceSchool && (
                    <span className="text-red-500 text-sm">
                      {errors.offerAcceptanceSchool.message}
                    </span>
                  )}
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  className="px-4 py-2 border rounded"
                  onClick={onClose}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                >
                  Save
                </button>
              </div>
            </form>
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  );
}

export default function OffersStep({ athleteId, onDone }) {
  const dispatch = useDispatch();
  const apiInstance = useAxiosInstance();
  const { offers } = useSelector((s) => s.athletes);

  const offersList = useMemo(
    () => (Array.isArray(offers) ? offers : []),
    [offers],
  );

  const [addOpen, setAddOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [hasAcceptedOffer, setHasAcceptedOffer] = useState(null);
  const [acceptanceModalOpen, setAcceptanceModalOpen] = useState(false);
  const [savedStatus, setSavedStatus] = useState("no");
  const [savedSchool, setSavedSchool] = useState("");

  useEffect(() => {
    if (!athleteId) return;
    dispatch(fetchOffers(apiInstance, athleteId));
    getOfferAcceptanceStatus(apiInstance, athleteId)
      .then((data) => {
        if (
          data?.offerAcceptanceStatus &&
          data.offerAcceptanceStatus !== "no"
        ) {
          setSavedStatus(data.offerAcceptanceStatus);
          setSavedSchool(data.offerAcceptanceSchool ?? "");
          setHasAcceptedOffer(true);
        } else {
          setHasAcceptedOffer(false);
        }
      })
      .catch(() => {
        setHasAcceptedOffer(false);
      });
  }, [dispatch, apiInstance, athleteId]);

  const openDelete = (id) => {
    setDeleteId(id);
    setDeleteOpen(true);
  };

  const doDelete = async () => {
    if (!deleteId) return;
    await dispatch(deleteOfferAction(apiInstance, deleteId));
    await dispatch(fetchOffers(apiInstance, athleteId));
    setDeleteOpen(false);
    setDeleteId(null);
  };

  const handleAcceptanceSubmit = (status, school) => {
    setSavedStatus(status);
    setSavedSchool(school);
    setHasAcceptedOffer(status !== "no");
    setAcceptanceModalOpen(false);
  };

  const handleAcceptanceClose = () => {
    setAcceptanceModalOpen(false);
    // Revert radio to reflect whatever was actually saved before the modal opened
    // savedStatus can be "", "no", "Committed", or "Signed" — only the latter two mean accepted
    setHasAcceptedOffer(!!savedStatus && savedStatus !== "no");
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex justify-end">
        <button
          type="button"
          className="bg-blue-700 text-white px-4 py-2 rounded text-sm hover:bg-blue-900"
          onClick={() => setAddOpen(true)}
        >
          <FontAwesomeIcon icon={faPlus} className="mr-2" />
          Add Offer
        </button>
      </div>

      {/* Offers table with sticky header */}
      <div className="border rounded-md overflow-hidden">
        <div className="max-h-[45vh] overflow-y-auto">
          <table className="min-w-full text-sm">
            <thead className="sticky top-0 z-10 bg-gray-50 border-b">
              <tr>
                <th className="text-left px-4 py-3 font-semibold">School</th>
                <th className="text-left px-4 py-3 font-semibold">Division</th>
                <th className="text-left px-4 py-3 font-semibold w-[120px]">
                  Year
                </th>
                <th className="text-right px-4 py-3 font-semibold w-[110px]">
                  Actions
                </th>
              </tr>
            </thead>

            <tbody>
              {offersList.length === 0 ? (
                <tr>
                  <td className="px-4 py-6 text-gray-600" colSpan={4}>
                    No offers found. Click <b>Add Offer</b> if you have received
                    one.
                  </td>
                </tr>
              ) : (
                offersList.map((o) => (
                  <tr key={o.id} className="border-b last:border-b-0">
                    <td className="px-4 py-3">{o.schoolName}</td>
                    <td className="px-4 py-3">{o.division}</td>
                    <td className="px-4 py-3 font-semibold">{o.offerYear}</td>
                    <td className="px-4 py-3 text-right">
                      <button
                        type="button"
                        className="border rounded px-3 py-2 text-sm text-red-700 hover:bg-red-50"
                        onClick={() => openDelete(o.id)}
                        title="Delete"
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <span className="text-sm font-semibold">
            Have you accepted any offers?
          </span>
          <label className="flex items-center gap-1 text-sm cursor-pointer">
            <input
              type="radio"
              name="acceptedOffer"
              value="yes"
              checked={hasAcceptedOffer === true}
              onChange={() => {}}
              onClick={() => {
                setHasAcceptedOffer(true);
                setAcceptanceModalOpen(true);
              }}
            />
            Yes
          </label>
          <label className="flex items-center gap-1 text-sm cursor-pointer">
            <input
              type="radio"
              name="acceptedOffer"
              value="no"
              checked={hasAcceptedOffer === false}
              onChange={() => {
                setHasAcceptedOffer(false);
                setSavedStatus("");
                setSavedSchool("");
              }}
            />
            No
          </label>
          {hasAcceptedOffer && savedStatus !== "no" && (
            <span className="text-sm text-gray-500 italic">
              {savedStatus === "Committed" ? "Verbal commitment" : "Signed LOI"}
              {savedSchool ? ` — ${savedSchool}` : ""}
              <button
                type="button"
                className="ml-2 text-blue-600 underline"
                onClick={() => setAcceptanceModalOpen(true)}
              >
                Edit
              </button>
            </span>
          )}
        </div>

        <button
          type="button"
          className="bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded text-sm"
          onClick={() =>
            onDone(
              hasAcceptedOffer ? savedStatus : "no",
              hasAcceptedOffer ? savedSchool : "",
            )
          }
        >
          Done adding offers
        </button>
      </div>

      <AddOfferModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        athleteId={athleteId}
        onSaved={async () => {
          await dispatch(fetchOffers(apiInstance, athleteId));
          setAddOpen(false);
        }}
      />

      <DeleteOfferModal
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        onConfirm={doDelete}
      />

      <OfferAcceptanceModal
        open={acceptanceModalOpen}
        onClose={handleAcceptanceClose}
        onSubmit={handleAcceptanceSubmit}
        initialValues={{
          offerAcceptanceStatus: savedStatus,
          offerAcceptanceSchool: savedSchool,
        }}
      />
    </div>
  );
}
